import { useState } from 'react';
import { Box, Chip, Divider, IconButton, Link, Tooltip, Typography, useTheme } from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { jobApplicationStatusLabels, jobApplicationSubStatusLabels } from '../statuses';
import ScoringScoreBadge from '@modules/scoring/components/ScoringScoreBadge';
import { ScoringStatus } from '@modules/scoring/types';
import ChangeJobApplicationStatusButton from '../components/ChangeJobApplicationStatusButton';

const APPLICATION_TYPE_LABELS: Record<string, string> = {
    talent: 'Candidature directe (profil)',
    email_apply: 'Candidature par email',
    quick_apply: 'Candidature rapide (sans compte)',
};

interface TalentContactSectionProps {
    talent: {
        email?: string;
        phoneNumber?: string;
    } | null | undefined;
    application: {
        _id: string;
        status?: string;
        subStatus?: string;
        date?: string;
        reference?: string;
        jobApplicationNumber?: string;
        type?: string;
        rating?: number | null;
        resumeScore?: number | null;
        scoringStatus?: string | null;
    } | null | undefined;
}

const CopyField = ({ label, value, href }: { label: string; value: string; href?: string }) => {
    const theme = useTheme();
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // clipboard not available
        }
    };

    return (
        <Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                {href ? (
                    <Link href={href} variant="body2" sx={{ color: theme.palette.neutralWhite.main, textDecorationColor: theme.palette.neutralWhite.main }}>
                        {value}
                    </Link>
                ) : (
                    <Typography variant="body2">{value}</Typography>
                )}
                <Tooltip title={copied ? 'Copié !' : 'Copier'} placement="top">
                    <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
                        {copied ? (
                            <i className="fi fi-br-check" style={{ fontSize: 14, color: theme.palette.successGreen.main, display: 'flex' }} />
                        ) : (
                            <i className="fi fi-br-copy" style={{ fontSize: 14, color: theme.palette.text.secondary, display: 'flex' }} />
                        )}
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};


const TalentContactSection = ({ talent, application }: TalentContactSectionProps) => {
    const theme = useTheme();

    const statusLabel = application?.status ? jobApplicationStatusLabels[application.status] ?? application.status : null;
    const subStatusLabel = application?.subStatus ? jobApplicationSubStatusLabels[application.subStatus] ?? application.subStatus : null;
    const scoringStatus = (application?.scoringStatus ?? 'pending') as ScoringStatus;

    const formattedDate = application?.date
        ? format(new Date(application.date), 'dd MMMM yyyy', { locale: fr })
        : null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Status */}
            {(statusLabel || subStatusLabel) && (
                <Box>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Statut
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                        <ChangeJobApplicationStatusButton
                            jobApplicationId={String(application._id)}
                            status={application.status}
                            subStatus={application.subStatus}
                        />
                    </Box>
                </Box>
            )}

            {/* AI Score */}
            <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Score IA
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                    <ScoringScoreBadge
                        score={application?.resumeScore ?? null}
                        status={scoringStatus}
                    />
                </Box>
            </Box>

            <Divider />

            {/* Application meta */}
            <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                    Candidature
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {formattedDate && (
                        <Box>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Date
                            </Typography>
                            <Typography variant="body2">{formattedDate}</Typography>
                        </Box>
                    )}
                    {application?.jobApplicationNumber && (
                        <CopyField label="N° candidature" value={application.jobApplicationNumber} />
                    )}
                    {application?.reference && (
                        <CopyField label="Référence" value={application.reference} />
                    )}
                    {application?.type && (
                        <Box>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Type
                            </Typography>
                            <Typography variant="body2">
                                {APPLICATION_TYPE_LABELS[application.type] ?? application.type}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default TalentContactSection;
